import { Address } from '@ton/core';
import {
  CurveConfig,
  CurveState,
  remainingTonCapacity,
  toJettons,
} from '@gong/curve-math';
import { useEffect, useMemo, useState } from 'react';

import { QUOTE_TTL_SECONDS, IS_TESTNET } from '../config';
import { api, Passport } from '../lib/api';
import { loadClaimableFees, type ClaimableFees } from '../chain/ton';
import { TokenAvatar } from './DiscoveryScreen';
import { Copy } from '../i18n';
import { formatJettons, formatTon, formatUnits, parseTon, progressBps, unixNow } from '../lib/format';
import { canTrade } from '../lib/phase';
import { currentAntiSnipeBps, currentSpot, quoteBuy, quoteOrError, quoteSell } from '../quotes/quote';
import { formatFeeSplitLine } from '../lib/feeSplits';
import { passportFromState } from '../quotes/passport';
import { PhaseBadge } from '../ui/PhaseBadge';
import { RiskNote } from '../ui/RiskNote';
import { SeedDevBuyLines } from '../ui/SeedDevBuyLines';

export type TradeSide = 'buy' | 'sell';

export interface TokenModel {
  id: string;
  demo: boolean;
  name: string;
  ticker: string;
  curve?: Address;
  minter?: Address | null;
  state: CurveState;
  config: CurveConfig;
  seedTon: bigint;
  devBuyTon: bigint;
  imageUrl?: string | null;
  passport?: Passport;
  deployer?: Address | null;
  channel?: Address | null;
  feeVault?: Address | null;
  locker?: Address | null;
  feeEarnings?: { total: string; deposits: number };
}

export function TokenScreen({
  copy,
  token,
  busy,
  error,
  onBuy,
  onSell,
  onShare,
  initialBuyAmount,
  session,
  onRefresh,
  walletAddress,
  refreshVersion,
  onClaim,
  onHarvest,
}: {
  copy: Copy;
  token: TokenModel;
  busy: boolean;
  error: string | null;
  onBuy: (tonIn: bigint) => void;
  onSell: (jettons: bigint) => void;
  onShare: () => void;
  initialBuyAmount?: string;
  session: string | null;
  onRefresh: () => void;
  walletAddress: string;
  refreshVersion: number;
  onClaim: () => Promise<boolean>;
  onHarvest: () => Promise<boolean>;
}) {
  const [side, setSide] = useState<TradeSide | null>(initialBuyAmount !== undefined ? 'buy' : null);
  const [amount, setAmount] = useState(initialBuyAmount ?? '0.1');
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [now, setNow] = useState(unixNow());
  const [fees, setFees] = useState<ClaimableFees | null>(null);
  const [feesError, setFeesError] = useState<string | null>(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [harvestSubmitted, setHarvestSubmitted] = useState(false);

  const connectedWallet = useMemo(() => {
    try { return walletAddress ? Address.parse(walletAddress) : null; }
    catch { return null; }
  }, [walletAddress]);
  const isMyLaunch = Boolean(connectedWallet && (
    token.deployer?.equals(connectedWallet) || token.channel?.equals(connectedWallet)
  ));

  useEffect(() => {
    setFees(null); setFeesError(null); setClaimSubmitted(false); setHarvestSubmitted(false);
    if (!isMyLaunch || !connectedWallet || !token.feeVault || !token.channel) return;
    let cancelled = false;
    setFeesLoading(true);
    void loadClaimableFees(token.feeVault, token.channel, connectedWallet)
      .then((value) => { if (!cancelled) setFees(value); })
      .catch(() => { if (!cancelled) setFeesError('Не удалось прочитать начисления из сети.'); })
      .finally(() => { if (!cancelled) setFeesLoading(false); });
    return () => { cancelled = true; };
  }, [isMyLaunch, connectedWallet, token.channel, token.feeVault, refreshVersion]);

  useEffect(() => {
    setSubscribed(false);
    if (!session || token.demo) return;
    const controller = new AbortController();
    void api<{ enabled: boolean }>(`/api/tokens/${encodeURIComponent(token.id)}/subscribe`, session, undefined, undefined, controller.signal)
      .then((value) => setSubscribed(value.enabled))
      .catch(() => undefined);
    return () => controller.abort();
  }, [session, token.id, token.demo]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(unixNow()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { state, config, ticker } = token;
  const tradable = canTrade(state.phase);
  const remaining = remainingTonCapacity(config, state);
  const threshold = config.graduationThreshold;
  const filled = threshold - remaining;
  const pct = progressBps(filled, threshold) / 100;
  const antiBps = currentAntiSnipeBps(state, now, config);
  const spot = currentSpot(state, config);

  const phaseCopy =
    state.phase === 'Initializing'
      ? copy.phaseInitializing
      : state.phase === 'Graduating'
        ? copy.phaseGraduating
        : state.phase === 'Graduated'
          ? copy.phaseGraduated
          : copy.phaseActive;

  return (
    <div className="scroll">
      {token.demo ? <div className="banner">{copy.demoHint}</div> : null}

      <div className="token-hero">
        <div className="token-art"><TokenAvatar name={ticker} image={token.imageUrl} /></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2>
            {token.name}{' '}
            {token.demo ? <span className="phase">{copy.demoBadge}</span> : null}
          </h2>
          <PhaseBadge phase={state.phase} />
        </div>
        <p className="hint" style={{ margin: '6px 0 0' }}>
          {phaseCopy}
        </p>
        <div className="progress" aria-label={copy.progress}>
          <span style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        <p className="hint">
          {copy.progress}: {formatTon(filled)} / {formatTon(threshold)}
        </p>
        <div className="stat-grid">
          <div className="stat">
            <span>{copy.spot}</span>
            <b>{formatUnits(spot, 9, 6)} TON</b>
          </div>
          <div className="stat">
            <span>{copy.sold}</span>
            <b>{formatJettons(state.sold, ticker, 1)}</b>
          </div>
        </div>
        {!token.demo ? (
          <p className="hint" style={{ marginTop: 10 }}>
            <span className="label">{copy.feeSplits}: </span>
            {formatFeeSplitLine(config)}
          </p>
        ) : null}
      </div>

      <div className="section-label">{copy.seedLine}</div>
      <SeedDevBuyLines
        copy={copy}
        ticker={ticker}
        lines={passportFromState(token.seedTon, token.devBuyTon, state)}
      />

      {isMyLaunch && token.feeVault ? <section className="fees-panel" aria-label="Комиссии запуска">
        <div className="section-label">Накопленные комиссии</div>
        <div className="group"><div className="cell"><span className="label">Заработано этим токеном · учтено индексатором</span><strong className="value">{token.feeEarnings ? formatTon(BigInt(token.feeEarnings.total), 9) : 'Данные пока недоступны'}</strong></div></div>
        <p className="hint">В показатель входят подтверждённые начисления из истории FeeVault. Он обновляется после догона сети и не равен остатку к выплате.</p>
        {state.phase === 'Graduated' && token.locker ? <>
          <p className="hint">После градуации комиссии пула нужно отдельно собрать из LP-локера. Сделать это может любой участник, поэтому не нужно ждать действий команды. Контракт сохранит исходную ликвидность.</p>
          <button type="button" className="btn secondary" disabled={busy || harvestSubmitted} onClick={() => {
            void onHarvest().then((sent) => { if (sent) setHarvestSubmitted(true); });
          }}>{busy ? 'Подтвердите в кошельке…' : harvestSubmitted ? 'Запрос отправлен' : 'Собрать комиссии пула'}</button>
          <p className="hint">Для вызова в кошельке потребуется отправить 1,2 TON на газ. Если прироста комиссий нет, выплата может быть нулевой. Дождитесь подтверждения и поступления в FeeVault, нажмите «Обновить», затем «Собрать комиссии» ниже.</p>
        </> : null}
        {feesLoading ? <p className="hint">Проверяем начисления в сети…</p> : feesError ? <div className="banner warn" role="alert">{feesError}</div> : fees ? <>
          {!fees.recipient.equals(connectedWallet!) ? <p className="hint">Получатель комиссий этого запуска был изменён. Подключённый кошелёк больше не может их забрать.</p> : <>
            <div className="group"><div className="cell"><span className="label">Доступно к сбору</span><strong className="value">{fees.claimable === null ? 'Сумма неизвестна' : formatTon(fees.claimable, 9)}</strong></div></div>
            <p className="hint">FeeVault суммирует начисления по кошельку со всех запусков; сбор переведёт весь доступный баланс за вычетом сетевой комиссии.</p>
            {fees.claimable === null ? <div className="banner warn">Этот FeeVault не умеет сообщать протокольный остаток. Попытка сбора может не найти начислений и потратить сетевую комиссию.</div> : null}
            {fees.isProtocolRecipient && fees.channelAccrued > 0n ? <div className="banner warn">Этот кошелёк также указан получателем протокола. Контракт сначала выдаёт протокольную долю; доля канала ({formatTon(fees.channelAccrued, 9)}) останется в FeeVault, пока получатель протокола не будет изменён.</div> : null}
            <button type="button" className="btn" disabled={busy || claimSubmitted || fees.claimable === 0n} onClick={() => {
              void onClaim().then((sent) => { if (sent) setClaimSubmitted(true); });
            }}>{busy ? 'Подтвердите в кошельке…' : claimSubmitted ? 'Транзакция отправлена' : 'Собрать комиссии'}</button>
            {claimSubmitted ? <p className="hint">После подтверждения в сети нажмите «Обновить», чтобы увидеть новый остаток.</p> : null}
          </>}
        </> : null}
      </section> : null}

      <div className="btn-row">
        <button type="button" className="btn secondary" onClick={onShare}>Поделиться</button>
        <button type="button" className="btn secondary" onClick={onRefresh}>Обновить</button>
      </div>
      {token.passport ? <section className="passport-panel"><div className="section-label">Паспорт запуска</div><div className="group">
        <div className="cell"><span className="label">Канал</span><span className="value">{token.passport.telegramChannel?.title ?? 'Не привязан'}</span></div>
        <div className="cell"><span className="label">Владелец канала</span><span className="value">{token.passport.telegramChannel?.ownerVerified ? 'Подтверждён' : 'Не подтверждён'}</span></div>
        <div className="cell"><span className="label">Подписчики / держатели</span><span className="value">{token.passport.telegramChannel?.subscribers ?? '—'} / {token.passport.holders}</span></div>
        <div className="cell"><span className="label">Запуски / градуации создателя</span><span className="value">{token.passport.deployerHistory.launches} / {token.passport.deployerHistory.graduations}</span></div>
        <div className="cell"><span className="label">Канал наблюдается с</span><span className="value">{token.passport.telegramChannel?.firstSeenAt ? new Date(token.passport.telegramChannel.firstSeenAt * 1000).toLocaleDateString() : '—'}</span></div>
      </div><p className="hint">Дата начала наблюдения не подтверждает возраст канала.</p>
      {token.passport.telegramChannel?.username ? <a className="text-link" href={`https://t.me/${token.passport.telegramChannel.username}`} target="_blank" rel="noreferrer">Открыть канал ↗</a> : null}</section> : null}
      {!token.demo ? <div className="token-links"><a className="text-link" href={`https://${IS_TESTNET ? 'testnet.' : ''}tonviewer.com/${token.minter?.toString() ?? token.id}`} target="_blank" rel="noreferrer">Контракт токена ↗</a>
        {session ? <button className="chip" disabled={subscriptionBusy} onClick={() => {
          setSubscriptionBusy(true); setSubscriptionError(null);
          void api(`/api/tokens/${encodeURIComponent(token.curve!.toRawString())}/subscribe`, session, { enabled: !subscribed })
            .then(() => setSubscribed(!subscribed)).catch(() => setSubscriptionError('Не удалось сохранить подписку. Попробуйте ещё раз.'))
            .finally(() => setSubscriptionBusy(false));
        }}>{subscribed ? 'Отслеживается ✓' : 'Отслеживать токен'}</button> : null}</div> : null}
      {subscriptionError ? <div className="banner warn">{subscriptionError}</div> : null}

      {antiBps > 0n ? (
        <div className="banner warn" style={{ marginTop: 12 }}>
          {copy.antiSnipe.replace('{bps}', antiBps.toString())}
        </div>
      ) : null}

      {tradable ? (
        <div className="btn-row">
          <button type="button" className="btn" onClick={() => setSide('buy')}>
            {copy.buy}
          </button>
          <button type="button" className="btn secondary" onClick={() => setSide('sell')}>
            {copy.sell}
          </button>
        </div>
      ) : (
        <p className="hint">{copy.notActiveTrade}</p>
      )}

      {side && tradable ? (
        <TradePanel
          copy={copy}
          side={side}
          amount={amount}
          setAmount={setAmount}
          state={state}
          config={config}
          ticker={ticker}
          now={now}
          busy={busy}
          onConfirm={(value) => (side === 'buy' ? onBuy(value) : onSell(value))}
        />
      ) : null}

      {error ? <div className="banner warn">{error}</div> : null}
      <RiskNote copy={copy} />
    </div>
  );
}

function TradePanel({
  copy,
  side,
  amount,
  setAmount,
  state,
  config,
  ticker,
  now,
  busy,
  onConfirm,
}: {
  copy: Copy;
  side: TradeSide;
  amount: string;
  setAmount: (value: string) => void;
  state: CurveState;
  config: CurveConfig;
  ticker: string;
  now: number;
  busy: boolean;
  onConfirm: (value: bigint) => void;
}) {
  const parsed = useMemo(() => {
    try {
      return side === 'buy' ? parseTon(amount) : toJettons(amount || '0');
    } catch {
      return 0n;
    }
  }, [amount, side]);

  const quoted = useMemo(() => {
    if (parsed <= 0n) {
      return null;
    }
    return side === 'buy'
      ? quoteOrError(() => quoteBuy(state, parsed, { now, config }))
      : quoteOrError(() => quoteSell(state, parsed, { now, config }));
  }, [parsed, side, state, config, now]);

  const remaining =
    quoted?.ok && 'deadline' in quoted.value ? Math.max(0, quoted.value.deadline - now) : QUOTE_TTL_SECONDS;
  const expired = Boolean(quoted?.ok && remaining <= 0);

  return (
    <div className="quote">
      <div className="section-label">{side === 'buy' ? copy.buy : copy.sell}</div>
      <div className="group">
        <div className="field">
          <label>{side === 'buy' ? copy.amountTon : copy.amountJettons}</label>
          <input value={amount} inputMode="decimal" onChange={(event) => setAmount(event.target.value)} />
        </div>
      </div>
      <div className="chips">
        {(side === 'buy' ? ['0.1', '0.5', '1'] : ['1000', '10000', '100000']).map((value) => (
          <button
            key={value}
            type="button"
            className={`chip${amount === value ? ' on' : ''}`}
            onClick={() => setAmount(value)}
          >
            {value}
          </button>
        ))}
      </div>

      {quoted && !quoted.ok ? <div className="banner warn">{quoted.error.message}</div> : null}
      {quoted?.ok ? (
        <div className="group">
          <div className="cell">
            <span className="label">{copy.youReceive}</span>
            <span className="value">
              {side === 'buy'
                ? formatJettons(quoted.value.minOut, ticker)
                : formatTon(quoted.value.minOut)}
            </span>
          </div>
          <div className="cell">
            <span className="label">{copy.minOut}</span>
            <span className="value muted">{quoted.value.minOut.toString()}</span>
          </div>
          <div className="cell">
            <span className="label">{copy.validUntil}</span>
            <span className="value countdown">{expired ? '0' : remaining}s</span>
          </div>
          <div className="cell">
            <span className="label">{copy.fees}</span>
            <span className="value">
              {formatTon(quoted.value.tradeFee)}
              {'antiSnipeFee' in quoted.value && quoted.value.antiSnipeFee > 0n
                ? ` + ${formatTon(quoted.value.antiSnipeFee)}`
                : ''}
            </span>
          </div>
        </div>
      ) : null}
      <p className="hint">
        {expired ? copy.quoteExpired : copy.quoteTtl.replace('{seconds}', String(QUOTE_TTL_SECONDS))}
      </p>
      <div className="btn-row">
        <button
          type="button"
          className="btn"
          disabled={busy || !quoted?.ok || parsed <= 0n}
          onClick={() => onConfirm(parsed)}
        >
          {busy ? copy.sending : copy.send}
        </button>
      </div>
    </div>
  );
}
